import { useEffect, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { Plus, Pencil, Trash2, X, Upload, Save, Image as ImageIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const inputCls = "w-full px-3 py-2 bg-purple-900/40 border border-purple-700/50 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-yellow-500";

export const ImageUploadField = ({ value, onChange, label, testid }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
    } catch { alert("Error al subir la imagen"); }
    setUploading(false);
  };
  return (
    <div>
      <label className="text-gray-400 text-xs mb-1 block">{label}</label>
      <div className="flex gap-2 items-center">
        <div className="w-14 h-14 rounded-lg bg-purple-900/50 border border-purple-700/50 overflow-hidden flex items-center justify-center flex-shrink-0">{value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} className="text-gray-500" />}</div>
        <div className="flex-1 space-y-1.5">
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://... o sube un archivo" className={inputCls} data-testid={testid} />
          <label className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-purple-700/50 hover:bg-purple-600/60 text-xs text-white cursor-pointer">
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-file`} />
            {uploading ? "Subiendo..." : <><Upload size={12} /> Subir desde dispositivo</>}
          </label>
        </div>
      </div>
    </div>
  );
};

const parseDecklist = (text) => {
  const out = { main: [], extra: [], side: [] };
  let cur = "main";
  (text || "").split("\n").forEach((line) => {
    const l = line.trim();
    if (!l) return;
    const low = l.toLowerCase();
    if (low.includes("main")) { cur = "main"; return; }
    if (low.includes("extra")) { cur = "extra"; return; }
    if (low.includes("side")) { cur = "side"; return; }
    const m = l.match(/^(\d+)\s*[xX]?\s+(.+)$/) || l.match(/^(.+?)\s+[xX]\s*(\d+)$/);
    if (m) {
      const qty = parseInt(m[1]) || parseInt(m[2]);
      const name = isNaN(parseInt(m[1])) ? m[1] : m[2];
      out[cur].push({ name: name.trim(), qty: qty || 1 });
    } else out[cur].push({ name: l, qty: 1 });
  });
  return out;
};

const decklistToText = (d) => {
  if (!d) return "";
  return ["main", "extra", "side"].filter((k) => d[k]?.length).map((k) => `# ${k.toUpperCase()}\n` + d[k].map((c) => `${c.qty}x ${c.name}`).join("\n")).join("\n\n");
};

const Field = ({ f, value, onChange, options }) => {
  if (f.type === "image") return <ImageUploadField label={f.label} value={value} onChange={onChange} testid={`admin-field-${f.key}`} />;
  if (f.type === "textarea" || f.type === "markdown") return <div className={f.full ? "sm:col-span-2" : ""}><label className="text-gray-400 text-xs mb-1 block">{f.label}{f.type === "markdown" && <span className="text-purple-400"> (Markdown)</span>}</label><textarea rows={f.rows || 4} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} font-mono`} placeholder={f.placeholder} data-testid={`admin-field-${f.key}`} /></div>;
  if (f.type === "decklist") return <div className="sm:col-span-2"><label className="text-gray-400 text-xs mb-1 block">{f.label} <span className="text-purple-400">(escribe "# Main", "# Extra", "# Side" y luego "3x Nombre de carta" por línea)</span></label><textarea rows={8} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} font-mono`} placeholder={"# Main\n3x Ash Blossom & Joyous Spring\n# Extra\n1x Accesscode Talker\n# Side\n2x Nibiru, the Primal Being"} data-testid={`admin-field-${f.key}`} /></div>;
  if (f.type === "select") return <div><label className="text-gray-400 text-xs mb-1 block">{f.label}</label><select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`admin-field-${f.key}`}>{f.optional && <option value="">— Ninguno —</option>}{(options || f.options || []).map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}</select></div>;
  if (f.type === "checkbox") return <label className="flex items-center gap-2 text-sm text-gray-300 mt-5"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-pink-500" data-testid={`admin-field-${f.key}`} /> {f.label}</label>;
  if (f.type === "tags") return <div className={f.full ? "sm:col-span-2" : ""}><label className="text-gray-400 text-xs mb-1 block">{f.label} <span className="text-purple-400">(separados por coma)</span></label><input value={Array.isArray(value) ? value.join(", ") : value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder={f.placeholder} data-testid={`admin-field-${f.key}`} /></div>;
  return <div className={f.full ? "sm:col-span-2" : ""}><label className="text-gray-400 text-xs mb-1 block">{f.label}</label><input type={f.type || "text"} step={f.step} value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} className={inputCls} placeholder={f.placeholder} data-testid={`admin-field-${f.key}`} /></div>;
};

export const AdminCrud = ({ title, endpoint, listKey, fields, columns, dynamicOptions = {}, listParams = "", idKey = "id" }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => axios.get(`${API}${endpoint}${listParams}`, { withCredentials: true }).then((r) => setItems(r.data[listKey] || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, [endpoint]);

  const openNew = () => { const d = {}; fields.forEach((f) => { if (f.default !== undefined) d[f.key] = f.default; }); setEditing(d); setError(""); };
  const openEdit = (item) => { const d = { ...item }; fields.forEach((f) => { if (f.type === "decklist") d[f.key] = decklistToText(item[f.key]); }); setEditing(d); setError(""); };

  const save = async () => {
    setSaving(true); setError("");
    const payload = {};
    fields.forEach((f) => {
      let v = editing[f.key];
      if (f.type === "decklist") v = parseDecklist(v);
      if (f.type === "tags" && typeof v === "string") v = v.split(",").map((s) => s.trim()).filter(Boolean);
      if (f.type === "select" && v === "") v = null;
      if (v !== undefined) payload[f.key] = v;
    });
    try {
      if (editing[idKey]) await axios.put(`${API}${endpoint}/${editing[idKey]}`, payload, { withCredentials: true });
      else await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      setEditing(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => `${x.loc?.slice(-1)[0]}: ${x.msg}`).join(", ") : "Error al guardar");
    }
    setSaving(false);
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.nombre || item.titulo || item.name}"?`)) return;
    await axios.delete(`${API}${endpoint}/${item[idKey]}`, { withCredentials: true });
    load();
  };

  return (
    <div data-testid={`admin-crud-${listKey}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white">{title} <span className="text-gray-500 text-sm">({items.length})</span></h2>
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-purple-600 text-white text-sm font-bold hover:shadow-lg hover:shadow-pink-500/30" data-testid={`admin-add-${listKey}`}><Plus size={16} /> Nuevo</button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <div className="text-center py-10 bg-purple-900/20 rounded-xl text-gray-400">Sin registros todavía</div>}
        {items.map((item) => (
          <div key={item[idKey]} className="flex items-center gap-3 p-3 rounded-xl bg-purple-900/20 border border-purple-800/40 hover:border-purple-600/60 transition" data-testid={`admin-item-${item[idKey]}`}>
            {columns.image && <div className="w-12 h-12 rounded-lg bg-black/40 overflow-hidden flex-shrink-0">{item[columns.image] && <img src={item[columns.image]} alt="" className="w-full h-full object-cover" />}</div>}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white truncate">{item[columns.title]}</p>
              <p className="text-xs text-gray-400 truncate">{columns.subtitle?.map((k) => item[k]).filter(Boolean).join(" · ")}</p>
            </div>
            <button onClick={() => openEdit(item)} className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" data-testid={`admin-edit-${item[idKey]}`}><Pencil size={14} /></button>
            <button onClick={() => remove(item)} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`admin-delete-${item[idKey]}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      {editing && createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="admin-form-modal">
          <div className="bg-gray-900 border border-purple-700 rounded-2xl p-6 max-w-2xl w-full my-8">
            <div className="flex items-center justify-between mb-5"><h3 className="text-lg font-bold text-yellow-400">{editing[idKey] ? "Editar" : "Nuevo"} · {title}</h3><button onClick={() => setEditing(null)} className="text-gray-400 hover:text-white"><X /></button></div>
            <div className="grid sm:grid-cols-2 gap-4">
              {fields.map((f) => <Field key={f.key} f={f} value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} options={dynamicOptions[f.key]} />)}
            </div>
            {error && <p className="text-red-400 text-sm mt-3" data-testid="admin-form-error">{error}</p>}
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="flex-1 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-800 text-sm">Cancelar</button>
              <button onClick={save} disabled={saving} className="flex-1 py-2 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-lg font-bold text-black text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="admin-form-save"><Save size={16} /> {saving ? "Guardando..." : "Guardar"}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
